import { faBullhorn } from "@fortawesome/free-solid-svg-icons/faBullhorn";
import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";
import { faEllipsisVertical } from "@fortawesome/free-solid-svg-icons/faEllipsisVertical";
import { faGear } from "@fortawesome/free-solid-svg-icons/faGear";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons/faMagnifyingGlass";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import { faTags } from "@fortawesome/free-solid-svg-icons/faTags";
import React, { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import { AtiraDropdown } from "../../../../components/AtiraDropdown";
import { AtiraIcon } from "../../../../components/AtiraIcon";
import { Button } from "../../../../components/Button";
import { TagDot } from "../../../../components/TagChip";
import { Text } from "../../../../components/Text";
import { TextWithTruncated } from "../../../../components/TextWithTruncated";
import { SettingsSubRoutes } from "../../../../model/app/types/SettingsSubRoutes.enum";
import { useAppSelector } from "../../../../redux/store";
import { tagSliceSelectors } from "../../../../redux/tag/tag.selector";
import { TestIDs } from "../../../../tests/types/testIds";
import { Rounded } from "../../../../theme/Rounded";
import { Spacing } from "../../../../theme/Spacing";
import { EntryTagsMenuPopup } from "../../../entries/components/EntryTagsMenuPopup";
import { Info } from "../../../../components/Info";

// Plain elements rather than Flex, which keeps data attributes to itself
const Option = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.s};
  min-width: 0;
`;

// GlobalStyle strips the start padding off every submenu title, which left
// this one row out of line with the plain items around it. 0.75rem is the
// padding antd gives those items.
const SubmenuOption = styled(Option)`
  padding-inline-start: 0.75rem;
`;

// The icons differ in width, so a fixed slot keeps the labels in one column
const IconSlot = styled.span`
  display: inline-flex;
  justify-content: center;
  width: 1rem;
  flex-shrink: 0;
`;

const OptionEnd = styled.span`
  display: flex;
  margin-inline-start: auto;
  flex-shrink: 0;
`;

const Header = styled.div`
  padding: 0 ${Spacing.xs};
`;

const MenuButton = styled(Button)`
  margin: 0;
  padding: ${Spacing.xs};
  justify-content: center;
  border-radius: ${Rounded.circle};
  width: 28px;
  height: 28px;

  &:hover {
    background-color: ${({ theme }) => theme.lightMain};
  }
`;

type WAChatsHeaderMenuProps = {
  isOrgMember: boolean;
  searchOpen: boolean;
  onToggleSearch: VoidFunction;
  filterTagIds: string[];
  onToggleFilterTag: (tagId: string) => void;
  onOpenCampaigns: VoidFunction;
  onCreateChat: VoidFunction;
};

export const WAChatsHeaderMenu: React.FC<WAChatsHeaderMenuProps> = ({
  isOrgMember,
  searchOpen,
  onToggleSearch,
  filterTagIds,
  onToggleFilterTag,
  onOpenCampaigns,
  onCreateChat,
}) => {
  const [open, setOpen] = useState(false);
  const [tagSearch, setTagSearch] = useState("");
  const keepOpenRef = useRef(false);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const tags = useAppSelector(tagSliceSelectors.selectTags);

  const onTagClick = useCallback(
    (tagId: string) => {
      keepOpenRef.current = true;
      onToggleFilterTag(tagId);
    },
    [onToggleFilterTag],
  );

  // Clicking an item closes the menu, but several tags are often picked in a row
  const onOpenChange = useCallback(
    (next: boolean, info: { source: "trigger" | "menu" }) => {
      const keepOpen = keepOpenRef.current;
      keepOpenRef.current = false;

      if (!next && info.source === "menu" && keepOpen) {
        return;
      }

      setOpen(next);

      if (!next) {
        setTagSearch("");
      }
    },
    [],
  );

  const query = tagSearch.trim().toLowerCase();
  const matchingTags = tags.filter((tag) =>
    tag.name.toLowerCase().includes(query),
  );

  const items = [
    {
      key: "search",
      label: (
        <Option data-testid={TestIDs.integration.whatsappMeta.chat.search.open}>
          <IconSlot>
            <AtiraIcon icon={faMagnifyingGlass} color="main" />
          </IconSlot>
          <Text color="main">
            {t(
              searchOpen
                ? "integration.whatsapp.chatsPanel.search.close"
                : "integration.whatsapp.chatsPanel.search.open",
            )}
          </Text>
        </Option>
      ),
      onClick: onToggleSearch,
    },
    ...(tags.length || filterTagIds.length
      ? [
          {
            key: "tags",
            label: (
              <SubmenuOption
                data-testid={
                  TestIDs.integration.whatsappMeta.chat.tagsFilter.button
                }
              >
                <IconSlot>
                  <AtiraIcon icon={faTags} color="main" />
                </IconSlot>
                <Text color="main">
                  {t("integration.whatsapp.chatsPanel.tagsFilter.open")}
                </Text>
              </SubmenuOption>
            ),
            children: [
              ...(matchingTags.length
                ? matchingTags.map((tag) => ({
                    key: `tag-${tag._id}`,
                    label: (
                      <Option
                        data-testid={TestIDs.integration.whatsappMeta.chat.tagsFilter.option(
                          tag.name,
                        )}
                      >
                        <IconSlot>
                          <TagDot $color={tag.color} />
                        </IconSlot>
                        <TextWithTruncated fitToWidth color="main">
                          {tag.name}
                        </TextWithTruncated>
                        {filterTagIds.includes(tag._id) ? (
                          <OptionEnd>
                            <AtiraIcon icon={faCheck} color="main" />
                          </OptionEnd>
                        ) : null}
                      </Option>
                    ),
                    onClick: () => onTagClick(tag._id),
                  }))
                : [
                    {
                      key: "tags-empty",
                      disabled: true,
                      label: (
                        <Text fontSize="s" color="darkerSub">
                          {t(
                            tags.length
                              ? "entries.table.entry.tags.noMatch"
                              : "entries.table.entry.tags.empty",
                          )}
                        </Text>
                      ),
                    },
                  ]),
              { type: "divider" as const, key: "tags-divider" },
              {
                key: "tags-manage",
                label: (
                  <Option>
                    <IconSlot>
                      <AtiraIcon icon={faGear} color="main" />
                    </IconSlot>
                    <Text color="main">
                      {t("entries.table.entry.tags.manage")}
                    </Text>
                  </Option>
                ),
                onClick: () => navigate(`/settings/${SettingsSubRoutes.TAGS}`),
              },
            ],
            popupRender: (menu: React.ReactNode) => (
              <EntryTagsMenuPopup
                menu={menu}
                search={tagSearch}
                onSearch={setTagSearch}
                searchTestId={
                  TestIDs.integration.whatsappMeta.chat.tagsFilter.search
                }
                header={
                  <Header>
                    <Info
                      info={t(
                        "integration.whatsapp.chatsPanel.tagsFilter.header",
                      )}
                    />
                  </Header>
                }
              />
            ),
          },
        ]
      : []),
    ...(!isOrgMember
      ? [
          {
            key: "campaigns",
            label: (
              <Option
                data-testid={TestIDs.integration.whatsappMeta.campaign.open}
              >
                <IconSlot>
                  <AtiraIcon icon={faBullhorn} color="main" />
                </IconSlot>
                <Text color="main">
                  {t("integration.whatsapp.meta.campaign.title")}
                </Text>
              </Option>
            ),
            onClick: onOpenCampaigns,
          },
          {
            key: "create-chat",
            label: (
              <Option
                data-testid={TestIDs.integration.whatsappMeta.chat.createChat}
              >
                <IconSlot>
                  <AtiraIcon icon={faPlus} color="main" />
                </IconSlot>
                <Text color="main">
                  {t("integration.whatsapp.createChat.modal.title")}
                </Text>
              </Option>
            ),
            onClick: onCreateChat,
          },
        ]
      : []),
  ];

  return (
    <AtiraDropdown
      items={items}
      open={open}
      onOpenChange={onOpenChange}
      placement="bottomRight"
      trigger={["click"]}
      // Otherwise a click collapses the open tags submenu, even though the
      // menu itself is kept open for the next tag
      menu={{ multiple: true }}
      destroyOnHidden
      dataTestId={TestIDs.integration.whatsappMeta.chat.headerMenu}
    >
      <MenuButton
        icon={faEllipsisVertical}
        info={t("common.options")}
        color="black"
        backgroundColor="white"
      />
    </AtiraDropdown>
  );
};
